import {
  Box,
  Button,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Spinner,
  useToast,
  UnorderedList,
  ListItem,
  Select,
  InputRightElement,
  FormErrorMessage,
  FormControl,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { TopDisplay } from "../../../components";
import { IoMdAdd as AddIcon } from "react-icons/io";
import { LuSearch as SearchIcon } from "react-icons/lu";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import styled from "@emotion/styled";
import { atom, useAtom } from "jotai";
import apiCall from "../../../utils/apiCall";
import { openAtom, modalAtom, notificationAtom, openNAtom } from "../../../context";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Loader } from "../../../customs";
import ReactPaginate from "react-paginate";
import { TbReload as ReloadIcon } from "react-icons/tb";
import { AxiosError } from "axios";
import { useDropzone } from "react-dropzone";
import apiCallFormData from "../../../utils/apiFormDataCall";
import { FaRegFile as FileIcon, FaTimes as CancelIcon } from "react-icons/fa";
import { IoIosCloseCircleOutline as CloseIcon } from "react-icons/io";
import { format } from "date-fns";
import { BsUpload as UploadIcon} from "react-icons/bs";


const src = `/public-assets/upload_icon.png`;

// Define the PaymentItem interface
interface PaymentItem {
  PaymentReference: string;
  ApplicantId: string;
  PaymentChannel: string;
  CustomerPhone: number;
  CustomerEmail: number;
  Amount: number;
  PaymentAction: string;
  PaymentStatus: string;
  PaymentDate: string;
  FullName: string;
  RecieptUrl: string;
  CreatedBy: string;
}

// Define the TempPopUpProps interface
interface TempPopUpProps {
  index: string; // or number, depending on your use case
  data: PaymentItem; // Use the PaymentItem interface defined earlier
}
interface ErrorResponse {
  Message?: string;
  message?: string;
}

const HeadStyle = {
  borderBottom: "1px solid var(--dark-green-color)",
  padding: "15px 5px",
};

const DataStyle = {
  padding: "5px 5px",
};

const ROWSTYLE = styled.tr`
  & > td:last-of-type > * {
    display: none;
  }
  :hover > td {
    background-color: var(--table-highlight);
    &:last-of-type > * {
      display: block;
    }
  }
  &.active > td:last-of-type > * {
    display: block !important;
  }
`;

const ProcessOffLinePaymentAtom = atom<number | null>(null);

const TempPopUp: React.FC<TempPopUpProps> = ({ index, data }) => {
    return <div>{/* Your TempPopUp content */}</div>;
};

const ProcessOffLinePayment = () => { 
  const [search, setSearch] = useState("");
  const [filterData, setFilterData] = useState<PaymentItem[]>([]);
  const queryClient = useQueryClient();
  const [, setModal] = useAtom(modalAtom);
  const [, setOpen] = useAtom(openAtom);

  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1); // ✅ make reactive

  // ✅ Fetch deposit payments per page
  const { data: allUploadedPayment, isLoading } = useQuery(
    ["all-exam-payment", pageNumber],
    async () => {
      const response = await apiCall().get(`Payment/GetAllPaymentHistory`, {
        params: {
          paymentAction: "Exam",
          pageNumber,
          pageSize,
          paymentChannel: "Deposit",
        },
      });

      // ✅ Update counts
      setTotalCount(response.data.TotalCount || 0);
      setPageCount(response.data.PageCount || 1);

      return response.data.reportItems as PaymentItem[];
    },
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (allUploadedPayment) {
      setFilterData(allUploadedPayment);
    }
  }, [allUploadedPayment]);

  // 🔍 Search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase();
    setSearch(val);
    const source = allUploadedPayment ?? [];
    setFilterData(
      val
        ? source.filter(
            (item: PaymentItem) =>
              item.ApplicantId?.toLowerCase().includes(val) ||
              item.FullName?.toLowerCase().includes(val) ||
              item.PaymentReference?.toLowerCase().includes(val)
          )
        : source
    );
  };

  // 📤 Modal handler
  const showUploadPaymentEvidence = () => {
    setModal({
      header: "Upload Payment Evidence",
      content: <UploadPaymentEvidence />,
    });
    setOpen(true);
  };

  // ✅ Pagination: API-based
  const handlePageClick = (event: { selected: number }) => {
    setPageNumber(event.selected + 1); // ReactPaginate is zero-based
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Box>
          <TopDisplay
            label="Upload Payment Evidence"
            button_array={[
              <Button
                bg="var(--dark-green-color)"
                color="var(--white-color)"
                leftIcon={<UploadIcon />}
                variant="outline"
                colorScheme="var(--dark-green-color)"
                onClick={showUploadPaymentEvidence}
              >
                Upload Payment Evidence
              </Button>,
            ]}
          />

          {/* 🔍 Search Bar */}
          <Flex gap="20px" wrap="wrap" mt="20px">
            <InputGroup
              h="40px"
              width="250px"
              bg="var(--white-color)"
              borderColor="var(--dark-green-color)"
            >
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                value={search}
                onChange={handleSearch}
                type="text"
                placeholder="Search"
              />
              <span
                style={{
                  position: "absolute",
                  zIndex: "1",
                  right: "10px",
                  cursor: "pointer",
                  top: "12px",
                }}
              >
                <ReloadIcon />
              </span>
            </InputGroup>
          </Flex>

          {/* 📊 Summary */}
          <Flex mt="25px" flexDir="column" gap="10px">
            <Text m="0" fontSize="1rem">
              All Uploaded Off-Line Payments
            </Text>
            <Text m="0" fontSize=".8rem">
              Page {pageNumber} of {pageCount} — showing {filterData.length} records of {totalCount} total deposits
            </Text>
          </Flex>

          {/* 🧾 Table */}
          {filterData.length > 0 ? (
            <TableContainer mt="20px">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th sx={HeadStyle}>Reference</Th>
                    <Th sx={HeadStyle}>ApplicantId</Th>
                    <Th sx={HeadStyle}>FullName</Th>
                    <Th sx={HeadStyle}>Channel</Th>
                    <Th sx={HeadStyle}>Phone</Th>
                    <Th sx={HeadStyle}>Email</Th>
                    <Th sx={HeadStyle}>Date</Th>
                    <Th sx={HeadStyle}>Amount</Th>
                    <Th sx={HeadStyle}>Purpose</Th>
                    <Th sx={HeadStyle}>Reciept</Th>
                    <Th sx={HeadStyle}>CreatedBy</Th>
                    <Th sx={HeadStyle}>Status</Th>
                    <Th sx={HeadStyle} w="50px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filterData.map((item, index) => (
                    <ROWSTYLE key={index}>
                      <Td sx={DataStyle}>{item.PaymentReference}</Td>
                      <Td sx={DataStyle}>{item.ApplicantId}</Td>
                      <Td sx={DataStyle}>{item.FullName}</Td>
                      <Td sx={DataStyle}>{item.PaymentChannel}</Td>
                      <Td sx={DataStyle}>{item.CustomerPhone}</Td>
                      <Td sx={DataStyle}>{item.CustomerEmail}</Td>
                      <Td sx={DataStyle}>
                        {format(new Date(item.PaymentDate), "dd/MM/yyyy")}
                      </Td>
                      <Td sx={DataStyle}>{item.Amount || "N/A"}</Td>
                      <Td sx={DataStyle}>{item.PaymentAction || "N/A"}</Td>
                      <Td sx={DataStyle}>
                        {item.RecieptUrl ? (
                          <a
                            href={item.RecieptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                            }}
                          >
                            View
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </Td>
                      <Td sx={DataStyle}>{item.CreatedBy || "N/A"}</Td>
                      <Td sx={DataStyle}>{item.PaymentStatus || "N/A"}</Td>
                      <Td sx={DataStyle} w="50px" h="50px">
                        <TempPopUp index={item.ApplicantId} data={item} />
                      </Td>
                    </ROWSTYLE>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Text>No Uploaded Evidence</Text>
          )}

          {/* 🔁 Pagination */}
          <StylePaginateBox>
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              forcePage={pageNumber - 1}
            />
          </StylePaginateBox>
        </Box>
      )}
    </>
  );
};



const StylePaginateBox = styled.div`
  width: 100%;
  & > ul {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin: 0;
    margin-top: 10px;
  }
  & > ul > li {
    display: inline;
    margin: 0 5px;
    padding: 2px 5px;
    border-radius: 3px;
    background-color: transparent;
    color: var(--dark-green-color);
  }
  & > ul > li.selected {
    background-color: var(--highlight-paginate-background);
    color: var(--highlight-paginate-color);
  }
  & > ul > li:first-of-type,
  & > ul > li:last-of-type {
    font-weight: bold;
  }
`;
 
const UploadSchema = Yup.object({
  StudentNo: Yup.string().trim().required("Student number is required"),
  // AdviceId: Yup.number().required("Advice ID is required"),
  ExamPeriodId: Yup.string().required('Exam Period is required'), // <-- Add validation
  // PaymentEvidence: Yup.mixed()
  //   .required("Please upload a file")
  //   .test("fileSize", "File size too large (max 5MB)", (value) => {
  //     if (value instanceof File) {
  //       return value.size <= 5 * 1024 * 1024;
  //     }
  //     return false;
  //   })
  //   .test("fileType", "Unsupported file format (JPG, PNG, PDF only)", (value) => {
  //     if (value instanceof File) {
  //       return ["image/jpeg", "image/png", "application/pdf"].includes(value.type);
  //     }
  //     return false;
  //   }),
});
      

const UploadPaymentEvidence = () => {
  const queryClient = useQueryClient();
  const [, set_open] = useAtom(openAtom);
  const [, set_Nopen] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const [studentNo, setStudentNo] = useState("");
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [examPeriodId, setExamPeriodId] = useState<number | null>(null);
  const [adviceId, setAdviceId] = useState<number | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
    
      // Fetch Exam Periods
      const { data: allExamPeriod } = useQuery("exam-period", async () => {
        const response = await apiCall().get("exam/setup/examPeriod");
        return response.data.Data;
      });
    
     
     

      const { refetch: fetchStudentData, isFetching: isFetchingStudent } = useQuery(
        ["fetchStudentData", studentNo, examPeriodId],
        async () => {
          const response = await apiCall().get("/Exam/pendingExamApplicationPayment", {
            params: { studentNo, ExamPeriodId: examPeriodId },
          });
          return response.data;
        },
        {
          enabled: false,
          onError: (err: any) => {
            setErrorMessage(err?.response?.data?.Message || "Error fetching data.");
          },
          onSuccess: (data) => {
            if (data?.Data) {
              setAmount(data.Data.Amount);
              setDescription(data.Data.Items[0]?.Description || "N/A");
              setPurpose(data.Data.Purpose);
              setAdviceId(data.Data.AdviceId);
            } else {
              setErrorMessage("No student found.");
            }
          },
        }
      );
      
      const handleCheckClick = () => {
        if (examPeriodId) {
          fetchStudentData();
        } else {
          setErrorMessage("Please select an exam period before checking.");
        }
      };
      
      const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
          setUploadedFiles(acceptedFiles);
        },
      });
    
      const removeFile=(name:any)=>{
        setUploadedFiles((files:any[]) => files?.filter(eFile => eFile.name != name));
      }
    
      const { mutate: addUpdate, isLoading } = useMutation({
        mutationKey: ["upload-payment-evidence"],
        mutationFn: async (data: FormData) => {
          return await apiCallFormData().post("/Exam/processOfflinePayment", data);
        },
        onSuccess: (data) => {
          set_notification({
            type: "success",
            message: data?.data?.Message || "Payment Evidence Uploaded Successfully",
          });
          set_Nopen(true);
          queryClient.invalidateQueries({ queryKey: ["payment-evidence"] });
          setUploadedFiles([]);
        },
        onError: (error: any) => {
          let errorMessage = "An error occurred. Please try again.";
    
          if (error?.response?.data) {
            const errorData = error.response.data;
            if (errorData.errors) {
              const validationErrors = Object.values(errorData.errors).flat().join(", ");
              errorMessage = validationErrors;
            } else {
              errorMessage = errorData?.Message || errorData?.title || errorMessage;
            }
          }
    
          set_notification({ type: "error", message: errorMessage });
          set_Nopen(true);
        },
        onSettled: () => {
          set_open(false);
        },
      });
      
      return (
        <>
        <Formik
      initialValues={{
        StudentNo: "",
        AdviceId: adviceId || "",
        PaymentEvidence: "",
        ExamPeriodId: '', // <-- Add this
    
      }}
      // enableReinitialize
      validationSchema={UploadSchema}
      onSubmit={(values) => {
        if (!uploadedFiles.length) {
          set_notification({ type: "error", message: "Select a file" });
          set_Nopen(true);
          return;
        }
  
        const formData = new FormData();
        formData.append("StudentNo", values.StudentNo);
        formData.append("AdviceId", adviceId?.toString() || "");
        formData.append("PaymentEvidence", uploadedFiles[0]);
  
    addUpdate(formData);
  }}
>
  {(formik: any) => {
    const { errors, touched, setFieldValue } = formik; // ✅ Extract `touched`
    
    return (
      <Form>
        <Flex flexDir="column" align="center" gap="20px">
          
          <FormControl isInvalid={!!errors.ExamPeriodId && touched.ExamPeriodId}>
              <Field
                as={Select}
                name="ExamPeriodId"
                placeholder="Exam Period"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setFieldValue("ExamPeriodId", e.target.value);
                  setExamPeriodId(Number(e.target.value));
                }}
                
            >
              {Array.isArray(allExamPeriod) && allExamPeriod.length > 0 ? (
                allExamPeriod.map((eType: any) => (
                  <option key={eType.Id} value={eType.Id}>
                    {eType.ExamName}
                  </option>
                ))
              ) : (
                <option value="">No data available</option>
              )}
            </Field>
            <FormErrorMessage>{errors.ActiveDiet}</FormErrorMessage>
          </FormControl>

          {/* Student No Input */}
          <FormControl isInvalid={!!errors.StudentNo && touched.StudentNo}>
            <InputGroup>
              <Field
                as={Input}
                placeholder="Student / Member ID"
                name="StudentNo"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("StudentNo", e.target.value);
                   setStudentNo(e.target.value); // Update studentNo state
                }}
              />
              <InputRightElement width="4.5rem">
                <Button
                  type="button"
                  h="1.75rem"
                  size="sm"
                  bg="var(--highlight-green)"
                  color="var(--white-color)"
                  onClick={handleCheckClick}
                  isLoading={isFetchingStudent}
                >
                  Check
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.StudentNo}</FormErrorMessage>
          </FormControl>

          {/* Error message display */}
          {errorMessage && <Text color="red">{errorMessage}</Text>}

          {/* Display Amount Details */}
          {amount !== null && (
            <Box
              mt={4}
              p={3}
              bg="gray.100"
              borderRadius="md"
              w="100%"
              border="2px solid"
              borderColor="green.500"
            >
              <Text>
                <strong>Amount:</strong> {amount}
              </Text>
              <Text>
                <strong>Description:</strong> {description}
              </Text>
              <Text>
                <strong>Purpose:</strong> {purpose}
              </Text>
            </Box>
          )}

          {/* File Upload Section */}
          <Box>Upload Document:</Box>
          <Box {...getRootProps()}>
            <input {...getInputProps()} name="PaymentEvidence" id="file" />
            <Image
              alt="Upload Evidence"
              src={src}
              w="100%"
              h="150px"
              objectFit="contain"
              objectPosition="center"
            />
            <UnorderedList sx={{ p: "0", m: "0", listStyleType: "none" }}>
              {uploadedFiles?.slice(0, 1).map((file: any) => (
                <ListItem
                  align="center"
                  gap="10px"
                  as={Flex}
                  sx={{ p: "5px 0" }}
                  fontSize="11px"
                  key={file.name}
                >
                  <FileIcon />
                  {file.name}
                  <CancelIcon
                    style={{ marginLeft: "auto" }}
                    onClick={() => removeFile(file.name)}
                  />
                </ListItem>
              ))}
            </UnorderedList>
          </Box>

          {/* Display Form Errors */}
          <Box>
            {errors &&
              Object.values(errors).map((eError: any, index: number) => (
                <Box color="red" key={index}>
                  {eError}
                </Box>
              ))}
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            bg="var(--dark-green-color)"
            color="var(--white-color)"
            disabled={isLoading}
          >
            {isLoading ? "Loading" : "Submit"}
          </Button>
        </Flex>
      </Form>
    );
  }}
</Formik>

        </>
      )
    }

// const UploadPaymentSubscription = () => {
//   const queryClient = useQueryClient();
//   const [, setOpen] = useAtom(openAtom);
//   const [, setNotification] = useAtom(notificationAtom);
//   const [, setNOpen] = useAtom(openNAtom);
//   const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
//   const [studentNo, setStudentNo] = useState("");
//   const [amount, setAmount] = useState<number | null>(null);
//   const [description, setDescription] = useState<string | null>(null);
//   const [purpose, setPurpose] = useState<string | null>(null);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [examPeriodId, setExamPeriodId] = useState<number | null>(null);
//   const [modal, setModal] = useState<{ header: string; content: React.ReactNode } | null>(null);

//   // Fetch Exam Periods
//   const { data: allExamPeriod } = useQuery("exam-period", async () => {
//     const response = await apiCall().get("exam/setup/examPeriod");
//     return response.data.Data;
//   });

//   // Fetch Student Payment Data
//   const { refetch: fetchStudentData, isFetching: isFetchingStudent } = useQuery(
//     ['fetchStudentData', studentNo, examPeriodId],
//     async () => {
//       const response = await apiCall().get('/Exam/pendingExamApplicationPayment', {
//         params: { studentNo, ExamPeriodId: examPeriodId },
//       });
//       return response.data;
//     },
//     {
//       enabled: false,
//       onError: (err: any) => {
//         setErrorMessage(
//           err?.response?.data?.Message || 'Error fetching data. Please check the student number.'
//         );
//       },
//       onSuccess: (data) => {
//         if (!data || !data.Data) {
//           setErrorMessage('No student found.');
//           setAmount(null);
//           setDescription(null);
//           setPurpose(null);
//         } else {
//           setErrorMessage('');
//           setAmount(data.Data.Amount);
//           setDescription(data.Data.Items[0]?.Description || "N/A");
//           setPurpose(data.Data.Purpose);
//         }
//       },
//     }
//   );

//   const handleCheckClick = () => {
//     if (examPeriodId) {
//       fetchStudentData(); 
//     } else {
//       setErrorMessage("Please select an exam period before checking.");
//     }
//   };

//   // Upload Component
//   const UploadOffLinePayment = () => {
//     return (
//       <Box p={4}>
//         <Text fontSize="lg" fontWeight="bold">Upload Payment Evidence</Text>
//         <Text>Drag & drop or select files for uploading.</Text>
//         <Box border="2px dashed" borderColor="gray.300" p={4} textAlign="center" cursor="pointer">
//           <input type="file" onChange={(e) => setUploadedFiles([...uploadedFiles, ...(e.target.files ? Array.from(e.target.files) : [])])} />
//         </Box>
//         <Button mt={4} colorScheme="blue" onClick={() => setOpen(false)}>
//           Close
//         </Button>
//       </Box>
//     );
//   };

//   // Show Modal Function
//   const showUploadOffLinePayment = () => {
//     setModal({
//       header: "Upload Payment Evidence",
//       content: <UploadOffLinePayment />,
//     });
//     setOpen(true);
//   };

//   return (
//     <>
//       <Formik
//         initialValues={{
//           StudentNo: "",
//           AdviceId: "",
//           PaymentEvidence: "",
//         }}
//         onSubmit={(values) => {
//           if (uploadedFiles.length > 0) {
//             const data = new FormData();
//             data.append('StudentNo', values.StudentNo);
//             data.append('AdviceId', values.AdviceId);
//             data.append('PaymentEvidence', uploadedFiles[0]);
//             addUpdate(data);
//           } else {
//             setNotification({
//               type: 'error',
//               message: 'Select a file'
//             });
//             setNOpen(true);
//           }
//         }}
//       >
//         {({ setFieldValue }) => (
//           <Form>
//             <Flex flexDir="column" align="center" gap="20px">
//               {/* Exam Period Dropdown */}
//               <FormControl>
//                 <Field as={Select} name="ActiveDiet" placeholder="Exam Diet" onChange={(e) => {
//                   setFieldValue("ActiveDiet", e.target.value);
//                   setExamPeriodId(Number(e.target.value));
//                 }}>
//                   {Array.isArray(allExamPeriod) && allExamPeriod.length > 0 ? (
//                     allExamPeriod.map((eType: any) => (
//                       <option key={eType.Id} value={eType.Id}>
//                         {eType.ExamName}
//                       </option>
//                     ))
//                   ) : (
//                     <option value="">No data available</option>
//                   )}
//                 </Field>
//               </FormControl>

//               {/* Student No Input */}
//               <FormControl>
//                 <InputGroup>
//                   <Field
//                     as={Input}
//                     placeholder="Student / Member ID"
//                     name="StudentNo"
//                     onChange={(e) => {
//                       setFieldValue("StudentNo", e.target.value);
//                       setStudentNo(e.target.value);
//                     }}
//                   />
//                   <InputRightElement width="4.5rem">
//                     <Button h="1.75rem" size="sm" colorScheme="green" onClick={handleCheckClick} isLoading={isFetchingStudent}>
//                       Check
//                     </Button>
//                   </InputRightElement>
//                 </InputGroup>
//               </FormControl>

//               {errorMessage && <Text color="red.500">{errorMessage}</Text>}
//               {amount !== null && (
//                 <Box mt={4} p={3} bg="gray.100" borderRadius="md" w="100%" border="2px solid" borderColor="green.500">
//                   <Text><strong>Amount:</strong> {amount}</Text>
//                   <Text><strong>Description:</strong> {description}</Text>
//                   <Text><strong>Purpose:</strong> {purpose}</Text>
//                 </Box>
//               )}

//               {/* Upload Payment Button */}
//               <Button variant="outline" colorScheme="blue" onClick={showUploadOffLinePayment}>
//                 Upload Payment Evidence
//               </Button>

//               {/* Submit Button */}
//               <Button type="submit" colorScheme="green">
//                 Submit
//               </Button>
//             </Flex>
//           </Form>
//         )}
//       </Formik>

//       {/* Modal */}
//       {modal && (
//         <Modal isOpen={true} onClose={() => setOpen(false)}>
//           <ModalOverlay />
//           <ModalContent>
//             <ModalHeader>{modal.header}</ModalHeader>
//             <ModalCloseButton />
//             <ModalBody>{modal.content}</ModalBody>
//           </ModalContent>
//         </Modal>
//       )}
//     </>
//   );
// };


export default ProcessOffLinePayment;